import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

interface IRestrictedCountriesResponse {
  countries: string[];
}

export const restrictedCountriesError = (error: unknown): string => {
  const payload = (error as AxiosError<{ error?: string; message?: string }>)
    ?.response?.data;
  return payload?.error || payload?.message ||
    (error instanceof Error ? error.message : "Please try again.");
};

// Backend requires READ_SYSTEM for GET and WRITE_SYSTEM for POST.
export const useRestrictedCountries = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const { addToast } = useToast();
  const endpoint = ADMIN_API_URL(AdminEndpointEnum.RESTRICTED_COUNTRIES);
  const swr = useSWR<IRestrictedCountriesResponse, AxiosError>(
    tokenId ? [endpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<IRestrictedCountriesResponse>(endpoint, queryConfig());
      return response.data;
    },
    { refreshInterval: 30000 }
  );

  const setRestrictedCountry = useCallback(
    async (countryCode: string, restricted: boolean): Promise<boolean> => {
      try {
        const response = await axios.post<{ success: boolean }>(
          endpoint,
          { country_code: countryCode.trim().toUpperCase(), restricted },
          queryConfig()
        );
        if (response.data?.success !== true) {
          throw new Error("Backend rejected the country restriction update");
        }
      } catch (error) {
        addToast({
          type: ToastEnum.SIMPLE,
          header: "Country restriction update failed",
          subheader: restrictedCountriesError(error),
          status: ToastStatusEnum.ERROR,
        }, 5000);
        return false;
      }

      addToast({
        type: ToastEnum.SIMPLE,
        header: "Country restrictions updated",
        subheader: `${countryCode} ${restricted ? "added to" : "removed from"} the blocked countries.`,
        status: ToastStatusEnum.SUCCESS,
      }, 4000);
      // Keep the list authoritative; a refresh failure is displayed as a read error.
      await swr.mutate().catch(() => undefined);
      return true;
    },
    [addToast, endpoint, queryConfig, swr]
  );

  return { ...swr, setRestrictedCountry };
};
